'use client';

import React, { useState } from 'react';
import { useUser } from '@mentimeter/user';
import type { TrackingContext } from '@mentimeter/http-clients';
import { useSearchParams } from '@mentimeter/next-navigation';
import { useSubscriptions } from '@mentimeter/billing';
import {
  ModalContainer,
  ModalRoot,
  ModalBody,
  ModalHeader,
} from '@mentimeter/ragnar-ui/modal';
import { useTranslations } from 'next-intl';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import { trackDropOutReason } from '../join-page/utils';
import { InviteError } from '../../errors/InviteError';
import {
  JoinErrorEnum,
  useGetErrorMessage,
} from '../join-page/handleErrorMessage';
import { JoinTeamModalContainer } from './JoinTeamModalContainer';
import type { AuthStateT, JoinModeT } from './utils';
import { useInvite } from './useInvite';
import { JoinTeamErrorModalContent } from './JoinTeamErrorModalContent';

export function JoinTeamModal({
  inviteSeriesId,
  trackingContext,
}: {
  inviteSeriesId?: string;
  trackingContext: TrackingContext;
}) {
  const [open, setOpen] = useState(true);
  const { user } = useUser();
  const { subscriptions } = useSubscriptions(!user);
  const searchParams = useSearchParams();
  const continuePath = searchParams.get('continuePath');
  const token = searchParams.get('token');
  const authState = searchParams.get('authState') as AuthStateT | null;
  const joinMode = searchParams.get('joinMode') as JoinModeT | null;
  const getErrorMessage = useGetErrorMessage();
  const t = useTranslations('onboarding.join_workspace');

  const {
    data: inviteDetails,
    isLoading: isFetchingInviteDetails,
    error: inviteDetailsError,
  } = useInvite(token, {
    onError: (e) => {
      const errorMessage = getErrorMessage({
        workspaceName: inviteDetails?.workspaceName as string,
        error: e.code,
      });
      if (user) {
        trackDropOutReason({
          reason: errorMessage.trackingDropoutReason,
          userId: user.id,
          teamName: 'Workspace',
          joinMode,
          authState,
          path: window.location.pathname,
        });
      }
    },
  });

  if (!user || !token) {
    return null;
  }

  if (!authState || !joinMode || isFetchingInviteDetails) {
    return null;
  }

  const inviteNotFound = !inviteDetails;

  return (
    <ModalRoot open={open} onOpenChange={setOpen}>
      <ModalContainer width={['100%', '100%', '800px']}>
        <ScreenReaderOnly>
          <ModalHeader>{t('join_team_header')}</ModalHeader>
        </ScreenReaderOnly>
        <ModalBody p={0}>
          {inviteNotFound ? (
            <JoinTeamErrorModalContent
              error={
                inviteDetailsError ||
                new InviteError(JoinErrorEnum.INVITE_NOT_FOUND)
              }
              teamName={t('workspace')}
            />
          ) : (
            <JoinTeamModalContainer
              token={token}
              authState={authState}
              joinMode={joinMode}
              inviteSeriesId={inviteSeriesId}
              user={user}
              subscriptions={subscriptions}
              trackingContext={trackingContext}
              inviteDetails={inviteDetails}
              closeModal={() => setOpen(false)}
              {...(continuePath && { continuePath })}
            />
          )}
        </ModalBody>
      </ModalContainer>
    </ModalRoot>
  );
}
